// Analyses of point series (prototype "Analizy"): averaging to a coarser step, the load duration curve, the week
// heat map, period statistics and a linear model against temperature. Pure, over 15-minute values the host returns.

export interface Sample {
  t: number;
  v: number | null;
  /** Share of the step that had data (0–1), for averages over a coarser step. */
  c?: number;
}

export type Step = "15m" | "1h" | "6h" | "1d";
export const STEPS: readonly Step[] = ["15m", "1h", "6h", "1d"];
const QUARTER = 15 * 60_000;
const NIGHT = (hour: number) => hour >= 22 || hour < 6;

/** Start of the step containing `t`, in the browser's calendar (days start at local midnight). */
export function stepStart(t: number, step: Step): number {
  const d = new Date(t);
  switch (step) {
    case "15m":
      return Math.floor(t / QUARTER) * QUARTER;
    case "1h":
      return new Date(
        d.getFullYear(),
        d.getMonth(),
        d.getDate(),
        d.getHours(),
      ).getTime();
    case "6h":
      return new Date(
        d.getFullYear(),
        d.getMonth(),
        d.getDate(),
        Math.floor(d.getHours() / 6) * 6,
      ).getTime();
    case "1d":
      return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  }
}

/** Average of the values in each step; a step without any value is a gap (null), never zero. */
export function average(samples: readonly Sample[], step: Step): Sample[] {
  const acc = new Map<number, { sum: number; n: number }>();
  for (const s of samples) {
    const k = stepStart(s.t, step);
    const a = acc.get(k) ?? { sum: 0, n: 0 };
    if (s.v !== null) {
      a.sum += s.v;
      a.n++;
    }
    acc.set(k, a);
  }
  const per = STEP_QUARTERS[step];
  return [...acc]
    .sort(([a], [b]) => a - b)
    .map(([t, a]) => ({ t, v: a.n === 0 ? null : a.sum / a.n, c: Math.min(1, a.n / per) }));
}

/** 15-minute values in a step, for the completeness of an average (a day counts 96, DST days aside). */
const STEP_QUARTERS: Record<Step, number> = { "15m": 1, "1h": 4, "6h": 24, "1d": 96 };

/** Sum of the values in each step (energy increments into energy per day, …); steps without any value are gaps. */
export function total(samples: readonly Sample[], step: Step): Sample[] {
  const acc = new Map<number, number | null>();
  for (const s of samples) {
    const k = stepStart(s.t, step);
    const prev = acc.get(k) ?? null;
    acc.set(k, s.v === null ? prev : (prev ?? 0) + s.v);
  }
  return [...acc].sort(([a], [b]) => a - b).map(([t, v]) => ({ t, v }));
}

export interface Duration {
  /** Values from the highest to the lowest. */
  sorted: number[];
  max: number;
  median: number;
  /** The value exceeded during 5 % of the time. */
  p95: number;
}

/** Load duration curve: how long the quantity stays above each value. */
export function duration(samples: readonly Sample[]): Duration | null {
  const sorted = samples
    .filter((s) => s.v !== null)
    .map((s) => s.v!)
    .sort((a, b) => b - a);
  if (sorted.length === 0) {
    return null;
  }
  return {
    sorted,
    max: sorted[0]!,
    median: sorted[Math.floor(sorted.length / 2)]!,
    p95: sorted[Math.floor(sorted.length * 0.05)]!,
  };
}

export interface HeatRow {
  day: number;
  /** Average per hour of the day, null where there is no value. */
  hours: (number | null)[];
}

/** Days × hours of the day, from hourly averages (prototype "Mapa cieplna"). */
export function heatmap(samples: readonly Sample[]): HeatRow[] {
  const rows = new Map<number, { sum: number[]; n: number[] }>();
  for (const s of samples) {
    if (s.v === null) {
      continue;
    }
    const day = stepStart(s.t, "1d");
    const row = rows.get(day) ?? {
      sum: Array(24).fill(0),
      n: Array(24).fill(0),
    };
    const h = new Date(s.t).getHours();
    row.sum[h] += s.v;
    row.n[h]++;
    rows.set(day, row);
  }
  return [...rows]
    .sort(([a], [b]) => a - b)
    .map(([day, r]) => ({
      day,
      hours: r.sum.map((s, h) => (r.n[h] === 0 ? null : s / r.n[h]!)),
    }));
}

export interface Stats {
  avg: number | null;
  max: number | null;
  min: number | null;
  /** Average at night (22–6) as a share of the average by day (6–22). */
  nightToDay: number | null;
  /** Share of the elapsed 15-minute steps that have a value. */
  coverage: number | null;
}

export function stats(
  samples: readonly Sample[],
  from: number,
  to: number,
): Stats {
  const v = samples.filter((s) => s.v !== null) as { t: number; v: number }[];
  const mean = (xs: { v: number }[]) =>
    xs.length === 0 ? null : xs.reduce((s, x) => s + x.v, 0) / xs.length;
  const night = mean(v.filter((x) => NIGHT(new Date(x.t).getHours())));
  const day = mean(v.filter((x) => !NIGHT(new Date(x.t).getHours())));
  const steps = Math.max(
    0,
    Math.floor((Math.min(to, Date.now()) - from) / QUARTER),
  );
  return {
    avg: mean(v),
    max: v.length ? Math.max(...v.map((x) => x.v)) : null,
    min: v.length ? Math.min(...v.map((x) => x.v)) : null,
    nightToDay:
      night !== null && day !== null && day !== 0 ? night / day : null,
    coverage:
      steps === 0
        ? null
        : Math.min(
            1,
            new Set(v.map((x) => stepStart(x.t, "15m"))).size / steps,
          ),
  };
}

export interface Fit {
  slope: number;
  intercept: number;
  r2: number;
  n: number;
}

/** Least-squares line y = slope·x + intercept with its R²; needs three points and some spread in x. */
export function linearFit(
  points: readonly { x: number; y: number }[],
): Fit | null {
  const n = points.length;
  if (n < 3) {
    return null;
  }
  const mx = points.reduce((s, p) => s + p.x, 0) / n;
  const my = points.reduce((s, p) => s + p.y, 0) / n;
  const sxx = points.reduce((s, p) => s + (p.x - mx) ** 2, 0);
  if (sxx === 0) {
    return null;
  }
  const slope = points.reduce((s, p) => s + (p.x - mx) * (p.y - my), 0) / sxx;
  const intercept = my - slope * mx;
  const ssTot = points.reduce((s, p) => s + (p.y - my) ** 2, 0);
  const ssRes = points.reduce(
    (s, p) => s + (p.y - (slope * p.x + intercept)) ** 2,
    0,
  );
  return { slope, intercept, r2: ssTot === 0 ? 1 : 1 - ssRes / ssTot, n };
}

/** Pairs of values of two daily series on the same days. */
export function pairs(
  x: readonly Sample[],
  y: readonly Sample[],
): { t: number; x: number; y: number }[] {
  const ys = new Map(y.filter((s) => s.v !== null).map((s) => [s.t, s.v!]));
  return x
    .filter((s) => s.v !== null && ys.has(s.t))
    .map((s) => ({ t: s.t, x: s.v!, y: ys.get(s.t)! }));
}

/** CSV of several series on a common time axis (ISO time, one column per series; empty cell = no value). */
export function toCsv(
  series: readonly { name: string; samples: readonly Sample[] }[],
  separator = ";",
): string {
  const times = [
    ...new Set(series.flatMap((s) => s.samples.map((x) => x.t))),
  ].sort((a, b) => a - b);
  const maps = series.map((s) => new Map(s.samples.map((x) => [x.t, x.v])));
  const esc = (s: string) =>
    /[";\n,]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  const head = ["time", ...series.map((s) => esc(s.name))].join(separator);
  const rows = times.map((t) =>
    [
      new Date(t).toISOString(),
      ...maps.map((m) => ((m.get(t) ?? null) === null ? "" : String(m.get(t)))),
    ].join(separator),
  );
  return [head, ...rows].join("\n");
}

/**
 * The typical course of a point (prototype "Typowy przebieg"): for each weekday and hour, the median of the hourly
 * averages of earlier weeks. Only where at least `minWeeks` weeks have a value; otherwise there is no typical value.
 */
export function typicalProfile(history: readonly Sample[], minWeeks = 2): (t: number) => number | null {
  const byKey = new Map<string, number[]>();
  for (const s of average(history, "1h")) {
    if (s.v === null) {
      continue;
    }
    const d = new Date(s.t);
    const key = `${d.getDay()}|${d.getHours()}`;
    byKey.set(key, [...(byKey.get(key) ?? []), s.v]);
  }
  const median = new Map<string, number>();
  for (const [key, vs] of byKey) {
    if (vs.length >= minWeeks) {
      const sorted = [...vs].sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      median.set(key, sorted.length % 2 ? sorted[mid]! : (sorted[mid - 1]! + sorted[mid]!) / 2);
    }
  }
  return (t) => {
    const d = new Date(t);
    return median.get(`${d.getDay()}|${d.getHours()}`) ?? null;
  };
}

/**
 * Hours well above the typical course (prototype "Anomalie"): at least `ratio` times the typical value and more than
 * `floor` above it, so near-zero baselines do not flag noise. Merged into spans.
 */
export function anomalies(actual: readonly Sample[], typical: (t: number) => number | null, ratio = 1.5, floor = 0): { from: number; to: number }[] {
  const out: { from: number; to: number }[] = [];
  for (const s of average(actual, "1h")) {
    const typ = typical(s.t);
    if (s.v === null || typ === null || s.v < typ * ratio || s.v - typ <= floor) {
      continue;
    }
    const last = out[out.length - 1];
    if (last && last.to === s.t) {
      last.to = s.t + 3_600_000;
    } else {
      out.push({ from: s.t, to: s.t + 3_600_000 });
    }
  }
  return out;
}

/**
 * Energy per heating degree-hour (prototype "na stopniogodzinę"): hourly energy over (base − temperature) where it
 * is cold enough to heat (more than half a degree below the base); other hours have no value.
 */
export function perDegreeHour(energyPerHour: readonly Sample[], temperature: readonly Sample[], base = 15): Sample[] {
  const temps = new Map(average(temperature, "1h").map((s) => [s.t, s.v]));
  return total(energyPerHour, "1h").map((s) => {
    const tmp = temps.get(s.t);
    const dh = tmp == null ? 0 : base - tmp;
    return { t: s.t, v: s.v === null || dh <= 0.5 ? null : s.v / dh };
  });
}
